import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { useEffect, useState } from 'react'
import { ConditionEditor } from './condition-editor'
import type { Condition, ConditionField, MatchMode } from './condition-filter-url'

const OPEN_AT_EVENT = 'condition-filter:open-at'

export function openConditionFilterAt(index: number) {
  window.dispatchEvent(new CustomEvent<number>(OPEN_AT_EVENT, { detail: index }))
}

export function ConditionFilterButton({
  fields,
  conditions,
  match,
  onApply,
  errors = {},
  companyId = null,
}: {
  fields: ConditionField[]
  conditions: Condition[]
  match: MatchMode
  onApply: (conditions: Condition[], match: MatchMode) => void
  errors?: Record<string, string>
  companyId?: string | null
}) {
  const { t } = useTranslation('condition-filter')
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState<number | null>(null)
  useEffect(() => {
    const openAtRow = (event: Event) => {
      setFocusIndex((event as CustomEvent<number>).detail)
      setOpen(true)
    }
    window.addEventListener(OPEN_AT_EVENT, openAtRow)
    return () => window.removeEventListener(OPEN_AT_EVENT, openAtRow)
  }, [])
  const close = () => setOpen(false)
  return (
    <div className="relative inline-block">
      <Button
        type="button"
        variant="outline"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setFocusIndex(null)
          setOpen(true)
        }}
      >
        {conditions.length ? t('buttonActive', { count: conditions.length }) : t('button')}
      </Button>
      {open ? (
        <section
          role="dialog"
          aria-label={t('title')}
          className="fixed inset-0 z-50 flex flex-col gap-4 overflow-y-auto bg-background p-5 shadow-xl sm:absolute sm:inset-auto sm:start-0 sm:top-full sm:mt-2 sm:max-h-[75vh] sm:w-[min(48rem,calc(100vw-2rem))] sm:rounded-lg sm:border sm:border-border"
        >
          <header className="flex items-center justify-between">
            <h2 className="text-heading-sm font-semibold">{t('title')}</h2>
            <Button type="button" variant="ghost" onClick={close}>
              {t('cancel')}
            </Button>
          </header>
          <ConditionEditor
            fields={fields}
            conditions={conditions}
            match={match}
            errors={errors}
            companyId={companyId}
            focusIndex={focusIndex}
            onApply={(nextConditions, nextMatch) => {
              onApply(nextConditions, nextMatch)
              close()
            }}
            onCancel={close}
          />
        </section>
      ) : null}
    </div>
  )
}
